import { ELEMENT_ATTRIBUTE_GROUP, ELEMENT_GROUPS } from './../utils';
import fonts from '../../../../utils/Fonts';

const ImageElementFields = {
  group: ELEMENT_GROUPS.MEDIA,
  label: 'Image',
  icon: 'PhotographIcon',
  description: 'Adds an image to the screen',
  properties: [
    'layout',
    {
      label: 'Display User Profile Image',
      name: 'displayUserProfileImage',
      editor: 'boolean',
      description: 'If true, displays the user profile image',
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'Show Uploaded Date',
      name: 'showUploadedDate',
      description:
        'If true, the date an image was uploaded will be shown below each image.',
      editor: 'switch',
      default: false,
      group: ELEMENT_ATTRIBUTE_GROUP.BEHAVIOUR
    },
    {
      label: 'Image Date Color',
      name: 'imageDateColor',
      editor: 'color',
      description: 'The color of the uploaded date text if displayed',
      default: '#000000',
      displayWhen: [{ field: 'showUploadedDate', operator: '=', value: true }],
      group: ELEMENT_ATTRIBUTE_GROUP.COLORS
    },
    {
      label: 'Text Color',
      name: 'color',
      editor: 'color',
      description: 'The color of the text if using a two column layout',
      default: '#000000',
      group: ELEMENT_ATTRIBUTE_GROUP.COLORS
    },
    {
      name: 'imageDateFontFamily',
      label: 'Image Date Font Family',
      description: 'The font family for the dates that appear below images',
      options: [{ label: '<Not Set>', value: '' }, ...fonts],
      editor: 'dropdown',
      type: 'string',
      group: ELEMENT_ATTRIBUTE_GROUP.FONTS
    },
    {
      name: 'imageDateFont',
      label: 'Image Date Font',
      description: '',
      editor: 'fontsize',
      type: 'font',
      default: 16,
      group: ELEMENT_ATTRIBUTE_GROUP.FONTS
    },
    'fontFamily',
    {
      label: 'Data Binding Source',
      name: 'datasourceId',
      description: 'Choose a content type this element will get its data from',
      editor: 'dropdown',
      function: {
        name: 'getDynamicDataSources',
        params: ['app.id']
      },
      default: '',
      displayWhen: [
        {
          field: 'databindingDisabled',
          operator: '!==',
          value: true
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'Data Binding Field',
      name: 'sourceField',
      editor: 'dropdown',
      description:
        'The field from the underlying datasource will be used as the value of this element',
      default: '',
      mediaLibraryStoreIn: 'custom_folder',
      mediaLibraryPath: 'apps',
      options: [],
      function: {
        name: 'getDynamicDataSourceFields',
        values: [],
        params: ['app.id', 'element.datasourceId']
      },
      displayWhen: [
        {
          field: 'databindingDisabled',
          operator: '!==',
          value: true
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'On Press Screen',
      name: 'action',
      description: 'A screen to navigate to if the image is pressed.',
      editor: 'dropdown',
      options: [],
      function: {
        name: 'getAppActions',
        params: ['app.id']
      },
      group: ELEMENT_ATTRIBUTE_GROUP.BEHAVIOUR
    },
    {
      label: '2 Columns',
      name: 'twoColumnLayout',
      editor: 'boolean',
      description: 'Display the image in two columns',
      group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
    },
    {
      label: 'Enable Image Viewer',
      name: 'enableImageViewer',
      editor: 'boolean',
      description:
        'If true, the image can be opened in a zoomable view when it is tapped',
      group: ELEMENT_ATTRIBUTE_GROUP.BEHAVIOUR
    },
    {
      label: 'Use Placeholder Image',
      name: 'usePlaceholderImage',
      editor: 'boolean',
      description:
        'If true, uses a placeholder image if there is no image in the underlying field',
      group: ELEMENT_ATTRIBUTE_GROUP.IMAGERY
    },
    {
      label: 'Placeholder Image',
      name: 'placeholderImage',
      editor: 'image',
      mediaLibraryStoreIn: 'custom_folder',
      mediaLibraryPath: 'apps',
      description: 'The placeholder image to use',
      displayWhen: [
        { field: 'usePlaceholderImage', operator: '=', value: true }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.IMAGERY
    },
    {
      label: 'Static Image',
      name: 'source',
      editor: 'image',
      mediaLibraryStoreIn: 'custom_folder',
      mediaLibraryPath: 'apps',
      description: 'The image to display.',
      displayWhen: [
        { field: 'datasourceId', operator: 'IS_EMPTY', value: true }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.IMAGERY
    },
    {
      label: 'Resize Mode',
      name: 'resizeMode',
      editor: 'dropdown',
      description:
        'The resize option of the image when it is displayed on the screen. Defaults to Cover.',
      default: 'cover',
      options: [
        { label: 'Contain', value: 'contain' },
        { label: 'Cover', value: 'cover' }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.IMAGERY
    },
    {
      label: 'Multi-Image Layout',
      name: 'multiImageLayout',
      editor: 'dropdown',
      description:
        'If the field that this image is bound to, contains multiple images, how should the images be laid out on the screen. Horizontal Stacked will layout the images side by side until they fill they width of the screen, then the next images will be laid out on a new row, starting from left to right.',
      default: 'cover',
      options: [
        { label: 'Stacked Vertically', value: 'column' },
        { label: 'Horizontal Wrapped', value: 'row_wrap' },
        { label: 'Horizontal No Wrap', value: 'row' }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.IMAGERY
    },
    {
      label: 'Use Original Image',
      name: 'useOriginalImage',
      editor: 'boolean',
      description:
        'If true, uses the original image that was uploaded instead of its generated thumbnail',
      group: ELEMENT_ATTRIBUTE_GROUP.IMAGERY
    },
    {
      label: 'Text Source Field',
      name: 'textSourceField',
      editor: 'dropdown',
      description:
        'The field from the underlying record that will be used for source of the text',
      default: '',
      options: [],
      function: {
        name: 'getDynamicDataSourceFields',
        params: ['app.id', 'datasourceId']
      },
      displayWhen: [
        { field: 'twoColumnLayout', operator: '=', value: true },
        {
          field: 'datasourceId',
          operator: 'IS_NOT_EMPTY',
          value: '',
          combine: 'AND'
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'Column Text',
      name: 'columnText',
      editor: 'textbox',
      description: 'The width of the image',
      default: '',
      displayWhen: [{ field: 'twoColumnLayout', operator: '=', value: true }],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'Font',
      name: 'columnFont',
      editor: 'fontsize',
      type: 'font',
      description: 'Customise the font for the second colmn',
      default: '',
      displayWhen: [{ field: 'twoColumnLayout', operator: '=', value: true }],
      group: ELEMENT_ATTRIBUTE_GROUP.FONTS
    },
    {
      label: 'Text Alignment',
      name: 'textAlignment',
      editor: 'alignment',
      description: 'The alignment of the text that appears next to the image',
      default: '',
      displayWhen: [{ field: 'twoColumnLayout', operator: '=', value: true }],
      group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
    },
    {
      label: 'Text Padding',
      name: 'textPadding',
      editor: 'boundary',
      description: 'The padding of the text that appears next to the image',
      default: '',
      displayWhen: [{ field: 'twoColumnLayout', operator: '=', value: true }],
      group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
    },
    {
      label: 'Image Width',
      name: 'width',
      editor: 'number',
      description: 'The width of the image',
      default: 80,
      group: ELEMENT_ATTRIBUTE_GROUP.IMAGERY
    },
    {
      label: 'Image Height',
      name: 'height',
      editor: 'number',
      description: 'The height of the image',
      default: 80,
      group: ELEMENT_ATTRIBUTE_GROUP.IMAGERY
    },
    {
      label: 'Border Width',
      name: 'borderWidth',
      editor: 'number',
      description: 'The border width of the iamge',
      default: 0,
      group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
    },
    {
      label: 'Border Radius',
      name: 'borderRadius',
      editor: 'number',
      description: 'The border radius of the image',
      default: 0,
      group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
    },
    {
      label: 'Border Color',
      name: 'borderColor',
      editor: 'color',
      description: 'The border color of the image',
      default: '',
      group: ELEMENT_ATTRIBUTE_GROUP.COLORS
    },
    {
      label: 'Background Color',
      name: 'backgroundColor',
      editor: 'color',
      description:
        'The background color of the image. Useful when an image is contained and doesnt fill the area',
      default: '',
      group: ELEMENT_ATTRIBUTE_GROUP.COLORS
    },
    {
      label: 'Border Style',
      name: 'borderStyle',
      editor: 'dropdown',
      description: 'The border style of the image',
      default: 'solid',
      options: [
        { label: 'Solid', value: 'solid' },
        { label: 'Dotted', value: 'dotted' },
        { label: 'Dashed', value: 'dashed' }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.COLORS
    },
    'alignment',
    'margin',
    'padding'
  ]
};

export default ImageElementFields;
